import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';

export interface ResponsibleOptionUser {
  _id: string;
  username: string;
  name: string;
  role: string;
}

interface ResponsiblePersonMultiSelectProps {
  users: ResponsibleOptionUser[];
  selectedNames: string[];
  onChange: (nextSelectedNames: string[]) => void;
  searchPlaceholder?: string;
  helperText?: string;
  /** 移除标签时的无障碍说明前缀，默认「负责人」 */
  removeMemberLabel?: string;
}

function formatResponsibleOptionLabel(user: ResponsibleOptionUser): string {
  if (user.username && user.username !== user.name) {
    return `${user.name}（${user.username}）`;
  }
  return user.name;
}

const ResponsiblePersonMultiSelect: React.FC<ResponsiblePersonMultiSelectProps> = ({
  users,
  selectedNames,
  onChange,
  searchPlaceholder = '搜索成员姓名/账号/角色',
  helperText,
  removeMemberLabel = '负责人'
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');

  const filteredUsers = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return users;
    return users.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const username = (user.username || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      return name.includes(keyword) || username.includes(keyword) || role.includes(keyword);
    });
  }, [users, searchKeyword]);

  const toggleResponsiblePerson = (personName: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selectedNames, personName]))
      : selectedNames.filter((name) => name !== personName);
    onChange(next);
  };

  return (
    <div style={{ border: '1px solid #ced4da', borderRadius: 6, padding: 10, background: '#fff' }}>
      <Form.Control
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        placeholder={searchPlaceholder}
        style={{ marginBottom: 8 }}
      />
      <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 6, padding: 8 }}>
        {filteredUsers.length === 0 ? (
          <div style={{ color: '#6c757d', fontSize: 13 }}>未找到匹配成员</div>
        ) : (
          filteredUsers.map((user) => (
            <Form.Check
              key={user._id}
              type="checkbox"
              id={`responsible-${user._id}`}
              label={`${formatResponsibleOptionLabel(user)}（${user.role}）`}
              checked={selectedNames.includes(user.name)}
              onChange={(e) => toggleResponsiblePerson(user.name, e.target.checked)}
              style={{ marginBottom: 4 }}
            />
          ))
        )}
      </div>
      {selectedNames.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {selectedNames.map((name) => (
            <span
              key={name}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1px solid #cfe2ff',
                background: '#e7f1ff',
                color: '#084298',
                borderRadius: 999,
                padding: '2px 8px',
                fontSize: 12
              }}
            >
              {name}
              <button
                type="button"
                onClick={() => toggleResponsiblePerson(name, false)}
                style={{
                  marginLeft: 6,
                  border: 'none',
                  background: 'transparent',
                  color: '#084298',
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontSize: 14
                }}
                aria-label={`移除${removeMemberLabel} ${name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {helperText && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#6c757d' }}>
          {helperText}
        </div>
      )}
    </div>
  );
};

export default ResponsiblePersonMultiSelect;
